/**
 * Hook para obter contagem de registros de uma entidade com cache
 *
 * Usado pelo SmartSelect para decidir automaticamente entre
 * busca client-side ou server-side no modo 'auto'.
 *
 * Features:
 * - Cache global com TTL de 5 minutos
 * - Compartilhado entre todas as instâncias do componente
 * - Pre-warming para entidades comuns
 *
 * @see SmartSelect.jsx - Componente que usa este hook
 * @see dropdownConfig.js - Configuração de thresholds
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { COUNT_CACHE_TTL_MS, getEntityConfig } from '@/config/dropdownConfig';

// =============================================================================
// CACHE GLOBAL
// =============================================================================

/**
 * Cache global de contagem de registros
 * Estrutura: Map<tableName, { count: number, timestamp: number }>
 */
const countCache = new Map<string, { count: number; timestamp: number }>();

/**
 * Obtém contagem do cache se ainda for válida
 * @param {string} table - Nome da tabela
 * @returns {number|null} Contagem ou null se cache expirado/inexistente
 */
const getCachedCount = (table: string): number | null => {
  const cached = countCache.get(table);
  if (cached && Date.now() - cached.timestamp < COUNT_CACHE_TTL_MS) {
    return cached.count;
  }
  return null;
};

/**
 * Armazena contagem no cache
 * @param {string} table - Nome da tabela
 * @param {number} count - Contagem de registros
 */
const setCachedCount = (table: string, count: number) => {
  countCache.set(table, {
    count,
    timestamp: Date.now()
  });
};

// =============================================================================
// HOOK PRINCIPAL
// =============================================================================

/**
 * Hook para obter contagem de registros de uma entidade
 *
 * @param {string} entity - Nome da entidade (deve existir em dropdownConfig)
 * @returns {Object} { count: number|null, loading: boolean, error: Error|null }
 *
 * @example
 * const { count, loading } = useEntityRowCount('pessoa');
 *
 * if (loading) return <Spinner />;
 * if (count > 1500) {
 *   // Usar busca server-side
 * }
 */
export const useEntityRowCount = (entity: string) => {
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    // Obter configuração da entidade
    const config = getEntityConfig(entity) as any;

    if (!config || !config.table) {
      setLoading(false);
      return;
    }

    const table = config.table as string;

    // Verificar cache primeiro
    const cachedCount = getCachedCount(table);
    if (cachedCount !== null) {
      setCount(cachedCount);
      setLoading(false);
      return;
    }

    // Buscar contagem do banco
    const fetchCount = async () => {
      try {
        const { count: rowCount, error: queryError } = await (supabase as any)
          .from(table)
          .select('*', { count: 'exact', head: true });

        if (queryError) {
          console.error(`[useEntityRowCount] Erro para ${table}:`, queryError);
          setError(queryError);
          setLoading(false);
          return;
        }

        if (rowCount !== null) {
          setCachedCount(table, rowCount);
          setCount(rowCount);
        }
      } catch (err) {
        console.error(`[useEntityRowCount] Exceção para ${table}:`, err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCount();
  }, [entity]);

  return { count, loading, error };
};

// =============================================================================
// UTILITÁRIOS
// =============================================================================

/**
 * Pre-aquece o cache de contagem para múltiplas tabelas
 * Útil para chamar no início da aplicação
 *
 * @param {string[]} tables - Lista de nomes de tabelas
 * @returns {Promise<void>}
 *
 * @example
 * // No App.jsx ou similar
 * useEffect(() => {
 *   preWarmCountCache(['pessoa', 'obra', 'cidade', 'produto']);
 * }, []);
 */
export const preWarmCountCache = async (tables: string[]): Promise<void> => {
  const promises = tables.map(async (table) => {
    // Pular se já tem cache válido
    if (getCachedCount(table) !== null) return;

    try {
      const { count, error } = await (supabase as any)
        .from(table)
        .select('*', { count: 'exact', head: true });

      if (!error && count !== null) {
        setCachedCount(table, count);
      }
    } catch (err) {
      // Falha silenciosa - cache simplesmente não será preenchido
      const msg = err instanceof Error ? err.message : String(err);
      console.warn(`[preWarmCountCache] Falha para ${table}:`, msg);
    }
  });

  await Promise.all(promises);
};

/**
 * Limpa o cache de contagem (útil para testes ou refresh forçado)
 * @param {string} table - Nome da tabela (opcional, se não informado limpa tudo)
 */
export const clearCountCache = (table: string | null = null) => {
  if (table) {
    countCache.delete(table);
  } else {
    countCache.clear();
  }
};

/**
 * Obtém contagem síncrona do cache (sem buscar)
 * Útil para verificações rápidas sem re-render
 *
 * @param {string} entity - Nome da entidade
 * @returns {number|null} Contagem cacheada ou null
 */
export const getCachedEntityCount = (entity: string): number | null => {
  const config = getEntityConfig(entity) as any;
  if (!config || !config.table) return null;
  return getCachedCount(config.table);
};

/**
 * Verifica se uma entidade deve usar server-side baseado no cache
 * Versão síncrona para uso fora de componentes React
 *
 * @param {string} entity - Nome da entidade
 * @returns {boolean|null} true/false ou null se cache não disponível
 */
export const shouldUseServerSideCached = (entity: string): boolean | null => {
  const config = getEntityConfig(entity) as any;
  if (!config) return null;

  // Estratégia fixa
  if (config.strategy === 'server') return true;
  if (config.strategy === 'client') return false;

  // Modo auto - verificar cache
  const count = getCachedEntityCount(entity);
  if (count === null) return null; // Cache não disponível

  const threshold = config.threshold || 500;
  return count > threshold;
};

export default useEntityRowCount;
